import { AskableAssetFormData, AskableImageModeType, CSSelectOption } from "types";
import { isNullOrUndefOrEmpty } from "@civicscience/chops";

/**
 * Askable asset format types.
 */
export const askableAssetFormat = {
  SVG: "svg",
  PNG: "png",
} as const;

/**
 * Askable asset format type.
 */
type AskableAssetFormat = (typeof askableAssetFormat)[keyof typeof askableAssetFormat];

/**
 * Askable asset format options for AskableAssetForm.
 */
export const askableAssetFormatOptions: Readonly<{ text: string; value: AskableAssetFormat }[]> = [
  { text: "SVG", value: askableAssetFormat.SVG },
  { text: "PNG", value: askableAssetFormat.PNG },
];

/**
 * Askable asset display types.
 */
export const askableAssetDisplay = {
  SHORT: "short",
  FULL: "full",
  QR: "qr",
  RESULTS: "results",
} as const;

/**
 * Askable asset display type.
 */
type AskableAssetDisplay = (typeof askableAssetDisplay)[keyof typeof askableAssetDisplay];

/**
 * Askable asset display options for AskableAssetForm.
 */
export const askableAssetDisplayOptions: Readonly<{ text: string; value: AskableAssetDisplay }[]> = [
  { text: "Image with question text only", value: askableAssetDisplay.SHORT },
  { text: "Image with question text and answer choices", value: askableAssetDisplay.FULL },
  { text: "Image with a QR code for this question", value: askableAssetDisplay.QR },
];

/**
 * Askable asset display options for AskableAssetForm.
 * Includes results option, which is conditionally allowed based on response count.
 */
export const askableAssetDisplayOptionsFull: Readonly<{ text: string; value: AskableAssetDisplay }[]> = [
  ...askableAssetDisplayOptions,
  { text: "Image with a results chart for this question", value: askableAssetDisplay.RESULTS },
];

/**
 * Provides a label for the display type in AskableAssetList.
 */
export const formatAskableAssetDisplay = (display: AskableAssetDisplay): string => {
  switch (display) {
    case askableAssetDisplay.SHORT:
      return "Image";
    case askableAssetDisplay.FULL:
      return "Full Image";
    case askableAssetDisplay.QR:
      return "QR";
    case askableAssetDisplay.RESULTS:
      return "Results Image";
    default:
      display satisfies never;
      return "";
  }
};

/**
 * Creates an AskableAssetForm with given overrides applied.
 */
export const makeAskableAssetForm = (overrides: Partial<AskableAssetFormData> = {}): AskableAssetFormData => ({
  name: "",
  accountId: null,
  askableId: 0,
  format: null,
  display: null,
  askableType: null,
  url: "",
  title: "",
  ...overrides,
});

export const minAskableImageResponseCount = 50;

/**
 * Determines if asset or askable image results mode is allowed based on response count.
 */
export const allowAskableImageResultsMode = (responseCount: number | null | undefined): boolean => {
  return !isNullOrUndefOrEmpty(responseCount) && responseCount >= minAskableImageResponseCount;
};

const imageModeOptions: CSSelectOption[] = [
  { text: "Basic", value: "basic" satisfies AskableImageModeType },
  { text: "Detailed", value: "detailed" satisfies AskableImageModeType },
];

const imageModeOptionsFull: CSSelectOption[] = [
  ...imageModeOptions,
  { text: "Results", value: "results" satisfies AskableImageModeType },
];

/**
 * Returns the appropriate askable image mode options based on response count.
 */
export const askableImageModeOptions = (responseCount: number | null | undefined): CSSelectOption[] => {
  return allowAskableImageResultsMode(responseCount) ? imageModeOptionsFull : imageModeOptions;
};
